import { useEffect, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Skeleton, useToast } from '@respectly/ui';
import type { AiModelPricingDto, AiProvider } from '@respectly/shared-types';
import { useAiOpsStore } from '../../stores/useAiOpsStore';

const PROVIDERS: AiProvider[] = ['azure_openai', 'foundry_agent', 'mistral'];

export function PricingTab() {
  const { pricing, loadingPricing, loadPricing } = useAiOpsStore();
  useEffect(() => {
    if (pricing.length === 0) void loadPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
        Rates are USD per 1,000,000 tokens (or per unit for page-billed models). Cost is snapshotted onto each call when
        it runs, so edits here only affect future calls. Seeded from public list prices (verified 2026-06-27) — Azure
        regional/data-zone deployments add ~10% and prompt caching discounts, so confirm against your Azure/Mistral contract.
      </div>

      <AddPricing />

      {loadingPricing && pricing.length === 0 ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : (
        <div className="space-y-3">
          {pricing.map((p) => <PricingRow key={p.id} row={p} />)}
        </div>
      )}
    </div>
  );
}

function PricingRow({ row }: { row: AiModelPricingDto }) {
  const { patchPricing, deletePricing } = useAiOpsStore();
  const { toast } = useToast();
  const [input, setInput] = useState(row.inputCostPer1M.toString());
  const [output, setOutput] = useState(row.outputCostPer1M.toString());
  const [unitCost, setUnitCost] = useState(row.unitCost?.toString() ?? '');
  const [active, setActive] = useState(row.isActive);
  const [busy, setBusy] = useState(false);

  const dirty = input !== row.inputCostPer1M.toString() || output !== row.outputCostPer1M.toString() || unitCost !== (row.unitCost?.toString() ?? '') || active !== row.isActive;

  const save = async () => {
    setBusy(true);
    try {
      await patchPricing(row.id, {
        inputCostPer1M: Number(input) || 0,
        outputCostPer1M: Number(output) || 0,
        unitCost: unitCost.trim() === '' ? null : Number(unitCost),
        isActive: active,
      });
      toast({ title: 'Pricing saved', description: `${row.provider}/${row.model}` });
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deletePricing(row.id);
      toast({ title: 'Pricing removed', description: `${row.provider}/${row.model}` });
    } catch (err) {
      toast({ title: 'Delete failed', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
      setBusy(false);
    }
  };

  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardContent className="flex flex-wrap items-end gap-4 p-4">
        <div className="min-w-[180px]">
          <div className="font-mono text-sm font-medium text-slate-900 dark:text-slate-50">{row.model}</div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            {row.provider}
            <Badge variant="outline" className="text-[10px]">{row.unit}</Badge>
            {!row.isActive && <span className="text-red-500">disabled</span>}
          </div>
        </div>
        <Num label="Input / 1M" value={input} onChange={setInput} />
        <Num label="Output / 1M" value={output} onChange={setOutput} />
        {row.unit !== 'tokens' && <Num label={`Cost / ${row.unit.replace(/s$/, '')}`} value={unitCost} onChange={setUnitCost} />}
        <label className="flex items-center gap-1.5 pb-2 text-xs text-slate-500">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> active
        </label>
        <div className="ml-auto flex gap-2 pb-1">
          <Button size="sm" disabled={!dirty || busy} onClick={save}><Save className="mr-1.5 h-3.5 w-3.5" /> Save</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={remove}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AddPricing() {
  const { savePricing } = useAiOpsStore();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState('');
  const [provider, setProvider] = useState<AiProvider>('azure_openai');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');

  const add = async () => {
    if (!model.trim()) return;
    try {
      await savePricing({ model: model.trim(), provider, inputCostPer1M: Number(input) || 0, outputCostPer1M: Number(output) || 0 });
      toast({ title: 'Model added', description: `${provider}/${model}` });
      setModel(''); setInput(''); setOutput(''); setOpen(false);
    } catch (err) {
      toast({ title: 'Add failed', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
    }
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Plus className="mr-1.5 h-3.5 w-3.5" /> Add model pricing
      </Button>
    );
  }
  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Add model pricing</CardTitle></CardHeader>
      <CardContent className="flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">Provider</span>
          <select value={provider} onChange={(e) => setProvider(e.target.value as AiProvider)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900">
            {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">Model / deployment</span>
          <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="gpt-4.1" className="h-9 w-48" />
        </label>
        <Num label="Input / 1M" value={input} onChange={setInput} />
        <Num label="Output / 1M" value={output} onChange={setOutput} />
        <div className="ml-auto flex gap-2 pb-1">
          <Button size="sm" onClick={add}>Add</Button>
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Num({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
      <Input type="number" step="0.0001" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-28" />
    </label>
  );
}
